import {
  AddressLookupTableAccount,
  PublicKey,
  TransactionInstruction,
  TransactionMessage,
  VersionedTransaction,
} from "@solana/web3.js";
import base58 from "bs58";
import { convertJupiterIxs } from "../hooks/utils/utils";

export interface JupiterInstruction {
  programId: string;
  accounts: Array<{ pubkey: string; isSigner: boolean; isWritable: boolean }>;
  data: string;
}

export interface JupiterBuildResponse {
  inputMint: string;
  outputMint: string;
  inAmount: string;
  outAmount: string;
  slippageBps: number;
  priceImpactPct?: number;
  computeBudgetInstructions: JupiterInstruction[];
  setupInstructions: JupiterInstruction[];
  swapInstruction: JupiterInstruction;
  cleanupInstruction: JupiterInstruction | null;
  otherInstructions: JupiterInstruction[];
  addressesByLookupTableAddress: Record<string, string[]> | null;
  blockhashWithMetadata: {
    blockhash: number[];
    lastValidBlockHeight: number;
  };
}

export function getJupiterPriceImpactLabel(build: JupiterBuildResponse): string {
  if (build.priceImpactPct != null) {
    return `${(build.priceImpactPct * 100).toFixed(2)}% Slippage`;
  }
  return `${(build.slippageBps / 100).toFixed(2)}% Slippage`;
}

export async function fetchJupiterBuild(
  apiUrl: string,
  params: {
    inputMint: string;
    outputMint: string;
    amount: string;
    taker: string;
    slippageBps: number;
  }
): Promise<JupiterBuildResponse> {
  const search = new URLSearchParams({
    inputMint: params.inputMint,
    outputMint: params.outputMint,
    amount: params.amount,
    taker: params.taker,
    slippageBps: String(params.slippageBps),
  });

  const response = await fetch(`${apiUrl}/kyros/jupiter/swap/build?${search}`);
  const data = await response.json();

  if (!response.ok || data.error) {
    throw new Error(
      typeof data.error === "string"
        ? data.error
        : data.message || `Jupiter API error (${response.status})`
    );
  }

  return data as JupiterBuildResponse;
}

export function jupiterLookupTablesFromBuild(
  raw: Record<string, string[]> | null
): AddressLookupTableAccount[] {
  if (!raw) return [];

  return Object.entries(raw).map(
    ([key, addresses]) =>
      new AddressLookupTableAccount({
        key: new PublicKey(key),
        state: {
          deactivationSlot: BigInt("18446744073709551615"),
          lastExtendedSlot: 0,
          lastExtendedSlotStartIndex: 0,
          addresses: addresses.map((a) => new PublicKey(a)),
        },
      })
  );
}

export function jupiterInstructionsFromBuild(build: JupiterBuildResponse): TransactionInstruction[] {
  const ixs: TransactionInstruction[] = [];

  ixs.push(...convertJupiterIxs(build.computeBudgetInstructions));
  ixs.push(...convertJupiterIxs(build.setupInstructions));
  ixs.push(...convertJupiterIxs([build.swapInstruction]));

  if (build.cleanupInstruction) {
    ixs.push(...convertJupiterIxs([build.cleanupInstruction]));
  }

  ixs.push(...convertJupiterIxs(build.otherInstructions));

  return ixs;
}

export function versionedTransactionFromJupiterBuild(
  build: JupiterBuildResponse,
  payer: PublicKey
): VersionedTransaction {
  const blockhash = base58.encode(Buffer.from(build.blockhashWithMetadata.blockhash));
  const addressLookupTableAccounts = jupiterLookupTablesFromBuild(
    build.addressesByLookupTableAddress
  );

  const message = new TransactionMessage({
    payerKey: payer,
    recentBlockhash: blockhash,
    instructions: jupiterInstructionsFromBuild(build),
  }).compileToV0Message(addressLookupTableAccounts);

  return new VersionedTransaction(message);
}
